import React from "react";
import {
	ColDef,
	ValueGetterParams,
	ICellRendererParams,
	ValueFormatterParams,
	CellClickedEvent,
	RowNode,
} from "ag-grid-community";
import orderby from "lodash.orderby";
import get from "lodash.get";
import { IForm, IFormField, ISelectFormField, isSystemForm, ClientFirebase, ISection } from "../../../laco-common";
import DataUtils from "../../../laco-common/helpers/data-utils";
import { asyncForEach } from "../../../laco-common/base/utils";
import { getForm, getDocument } from "../../../laco-common/base/firestore-utils";
import { IFormSubmission } from "../../../laco-common/base/models/formSubmission";
import { NUMERIC_COL_DEF, getBooleanColDef, ID_COL_DEF } from "./index";
import { IFormsGridContext } from "../columns";
import { CorreColDef } from "../colDef";
import { PhoneLinkWeb } from "../../ui/PhoneLinkWeb";
import { EmailLinkWeb } from "../../ui/EmailLinkWeb";
import { getCustomTooltipDrillColDef, getCustomTooltipImageColDef, getCustomTooltipContentColDef } from "./custom";
import { UICMSContent } from "../../ui/UICMSContent";
import { CustomTooltipDisplayMode } from "../CustomTooltip";
import { getDateColDef } from "./date";
import { ICMSRoleContext } from "../../../admin/contexts/cms-role/CMSRole";
import { SelectOption } from "../../../laco-common/base/form-validation/dynamic";
import { getFormColDefinitions } from "./forms";
import GridUtils from "../grid-utils";
import { IFirebaseContext } from "../../../laco-common/base/firebase";
import { getFieldKey } from "../../../laco-common/forms/render";
import { stringify } from "../../../utils";
import { OrderDetails } from "../../../laco-common/base/models/order";
import { Address } from "../../../laco-common/base/models/address";

export type ColumnConfigurationMap = { [key: string]: Partial<IFormField> };

export const getUserCols = (omitUid?: boolean): ColDef[] => {
	const uidCols: ColDef[] = omitUid
		? []
		: [
				{
					field: "uid",
					colId: "uid",
					headerName: "uid",
					headerTooltip: "User Id",
					// enableRowGroup: true
				},
		  ];
	return [
		...uidCols,
		{
			field: "email",
			colId: "email",
			headerName: "Email",
			headerTooltip: "Email",
			valueGetter: (params: ValueGetterParams) => {
				if (!params || !params.data) return "";
				return (
					params.data["user-email"] ||
					params.data.email ||
					(params.data as IFormSubmission).formSubmissionUserDetails?.email
				);
			},
			cellRendererFramework: (params: ICellRendererParams) => {
				return <EmailLinkWeb email={params.value} />;
			},
			minWidth: 300,
		},
		{
			field: "name",
			colId: "name",
			headerName: "Name",
			headerTooltip: "Name",
			valueGetter: (params: ValueGetterParams) => {
				if (!params || !params.data) return "";
				return (
					params.data["user-name"] ||
					params.data.name ||
					(params.data as IFormSubmission).formSubmissionUserDetails?.name
				);
			},
			minWidth: 300,
		},
	];
};

export const getColDefsFromForm = (form: IForm): ColDef[] => {
	let cols: ColDef[] = [];

	const addFields = (fields: IFormField[]) => {
		fields.forEach((field: IFormField) => {
			cols.push(getColDef(field));
			if (field.fields) {
				(field as ISelectFormField).selectValues.forEach((value: string | number) => {
					const arr: IFormField[] = new Array(Object.keys(field.fields).length);
					Object.keys(field.fields || {}).forEach((key: string) => {
						const nestedField: IFormField = field.fields[key];
						arr[nestedField.order] = { ...nestedField, key } as any;
					});
					arr.forEach((nestedField: IFormField) => {
						const col: ColDef = getColDef(field);
						const fieldKey: string = `${field.id}-${value}-${(nestedField as any).key}`;
						console.debug("fieldKey:", fieldKey);
						cols.push({
							...col,
							field: fieldKey,
							colId: fieldKey,
							headerName: `${value} - ${nestedField.label || nestedField.placeholder}`,
							headerTooltip: `${value} - ${nestedField.label || nestedField.placeholder}`,
							valueGetter: (params: ValueGetterParams) => {
								if (!params?.data) return "";
								if (params.data[fieldKey]) return params.data[fieldKey];
								const parentFieldDataArr: string[] = params.data[field.id];
								if (!parentFieldDataArr?.length) return "";
								if (!Array.isArray(parentFieldDataArr)) {
									return params.data[fieldKey];
								}
								const answer: string = Array.isArray(parentFieldDataArr)
									? parentFieldDataArr.find((k) => k.startsWith(value as string))?.toLowerCase()
									: (parentFieldDataArr as string).toLowerCase();
								if (!answer) return "";
								const nameField: string = (nestedField.label || nestedField.placeholder)?.toLowerCase();
								const startSearchFromIndex: number = answer.indexOf(nameField);
								if (startSearchFromIndex < 0) return "";
								let valueAsStr: string = answer
									.substr(startSearchFromIndex + nameField.length + 1)
									?.trimStart();
								const stringParts = valueAsStr.split(" ");
								if (!stringParts?.length) return "";
								valueAsStr = stringParts[0];
								return valueAsStr?.trim();
							},
						});
					});
				});
			}
		});
	};

	if (form.productPrice) {
		cols = cols.concat(getFormPaymentColDefs());
	}
	addFields(form.fields);

	(form.sections || []).forEach((section: ISection) => {
		addFields(section.fields);
	});

	console.log("cols:", cols);
	return cols;
};

export const getColDef = (field: IFormField): ColDef => {
	const name: string = field.label || field.placeholder || field.id;
	const key: string = getFieldKey(field);
	let col: ColDef | CorreColDef = {
		...(field as any),
		field: key,
		// colId: `${field.id}-${form.id}`,
		colId: `${field.id}`,
		headerName: name,
		headerTooltip: name,
	};

	if (col.valueGetter) {
		try {
			col.valueGetter = eval(col.valueGetter as string);
		} catch (e) {}
	}

	switch (field.fieldType) {
		case "boolean": {
			col = getBooleanColDef(col);
			break;
		}
		case "number": {
			col = {
				...col,
				...NUMERIC_COL_DEF,
			};
			break;
		}
		case "date":
		case "datetime": {
			col = {
				...col,
				...getDateColDef(field.id),
			};
			break;
		}
		case "phone": {
			col = {
				...col,
				cellRendererFramework: (params: ICellRendererParams) => {
					return <PhoneLinkWeb number={params.value} />;
				},
			};
			break;
		}
		case "email": {
			col = {
				...col,
				cellRendererFramework: (params: ICellRendererParams) => {
					return <EmailLinkWeb email={params.value} />;
				},
			};
			break;
		}
		case "link": {
			col = {
				...col,
				cellClass: "link",
				cellRendererFramework: (params: ICellRendererParams) => {
					return (
						<a href={params.value} className="text-xs" target="_blank">
							{params.value}
						</a>
					);
				},
				suppressAutoSize: true,
				width: 300,
			};
			break;
		}
		case "imageUrl": {
			col = getCustomTooltipImageColDef(col as CorreColDef, field);
			break;
		}
		case "address": {
			col.cellRendererFramework = (params: ICellRendererParams) => {
				console.log("address params:", params);
				const address: Address = params.value as Address;
				return address?.formatted_address;
			};
			break;
		}
		case "focusRow": {
			col.valueFormatter = (params: ValueFormatterParams) => {
				let value = params.value;
				if (params.api && params.value) {
					const found = GridUtils.findNode(params.api, (node: RowNode) => node.data.id === params.value);
					if (found) value = found.data.title;
				}
				return value;
			};
			col.cellClassRules = { "gray-link": (params) => params.value };
			(col as CorreColDef).listViewOmitCellClass = true;
			col.onCellClicked = (params: CellClickedEvent) => {
				console.log("params:", params);
				if (params.api && params.data) {
					GridUtils.focusRowApi(params.api, { id: params.value });
				}
			};
			break;
		}
		case "collection": {
			const selectField: ISelectFormField = field as ISelectFormField;
			if (selectField.collectionName) {
				col = getCustomTooltipDrillColDef(col as CorreColDef, field, selectField.findDrillItem); // banana
			} else {
				col = {
					...col,
					valueFormatter: (params: ValueFormatterParams) => {
						if (params.value && Array.isArray(params.value)) {
							return params.value.join(", ");
						} else {
							// debugger;
						}
						return params.value;
					},
				};
			}
			break;
		}
		case "select":
		case "chips": {
			const selectField: ISelectFormField = field as ISelectFormField;
			if (
				Array.isArray(selectField.selectValues) &&
				selectField.selectValues.length &&
				typeof selectField.selectValues[0] === "object"
			) {
				col.valueGetter = (params: ValueGetterParams) => {
					const value = get(params.data, selectField.id);
					const getFoundDisplay = (val: string): string => {
						const found: SelectOption = (selectField.selectValues as SelectOption[]).find(
							(option: SelectOption) => option.value === val
						);
						if (found) return found.label;
						return val;
					};
					if (Array.isArray(value)) {
						const arr = value as string[];
						const displayArr: string[] = [];
						arr.forEach((s) => {
							displayArr.push(getFoundDisplay(s) || s);
						});
						return displayArr.join(", ");
					}
					return getFoundDisplay(value);
				};
			} else {
				col.valueFormatter = (params: ValueFormatterParams) => {
					const value = get(params.data, selectField.id);
					if (Array.isArray(value)) return value.join(", ");
					return value;
				};
			}
			break;
		}
		case "html": {
			col = getCustomTooltipContentColDef(
				{ ...col, ignoreInDetailView: true } as CorreColDef,
				field,
				(params: ICellRendererParams) => {
					if (params.value) return <span className="gray-link">Preview Content</span>;
					return null;
				},
				(params) => {
					return <UICMSContent content={params.value} />;
				},
				(params) => {
					return <UICMSContent content={params.value} />;
				}
			);
			break;
		}
		case "color": {
			col.cellStyle = (params) => {
				return {
					color: params.value,
				};
			};
			col.cellClass = "cell-color";
			break;
		}
		case "list": {
			// field.listType
			col.cellRendererFramework = (params) => {
				if (!params?.data?.id) return <span />;
				return (
					<span
						className="link"
						onClick={() => {
							(document.querySelector(`[aria-label="View #${params.data.id}"]`) as any)?.click();
						}}
					>
						View
					</span>
				);
			};
			(col as CorreColDef).listViewRenderer = (params) => {
				// return stringify(params.value, true);
				// iterate on obj
				return (
					<ul className="p-0" style={{ listStyleType: "none" }}>
						{(params.value || []).map((o, i: number) => {
							return (
								<li key={i}>
									<span
										className="font-weight-bold"
										style={{
											padding: "5px 10px",
											borderRadius: "50%",
											backgroundColor: "var(--color-primary)",
											color: "#fff",
										}}
									>
										{i + 1}
									</span>
									{typeof o == "object" ? (
										<ul>
											{Object.keys(o).map((key: string, k: number) => {
												return (
													<li key={k}>
														<b style={{ textTransform: "capitalize" }}>{key}: </b>
														{stringify(o[key], true, key)}
													</li>
												);
											})}
										</ul>
									) : (
										stringify(o, true)
									)}
								</li>
							);
						})}
					</ul>
				);
			};
			break;
		}
		default: {
			if (field.id === "desc" || field.id === "description") {
				col = getDescriptionColConfig(col);
			}
		}
	}

	return col;
};

export async function getFormSubmissionColDefs(
	context: IFirebaseContext,
	formIds: string[],
	isAdmin: boolean
): Promise<{ colDefs: ColDef[]; formsGridContext: IFormsGridContext }> {
	let cols: ColDef[] = [];
	if (isAdmin) {
		cols.push({
			field: "formId",
			colId: "formId",
			headerName: "Form Id",
			headerTooltip: "Form Id",
		});
	}
	cols.push({
		field: "formName",
		colId: "formName",
		headerName: "Form Name",
		headerTooltip: "Form Name",
		valueGetter: (params: ValueGetterParams) => {
			if (!params || !params.data) return "";
			const formId: string = (params.data as IFormSubmission).formId;
			return (params.context as IFormsGridContext)?.formConfigMap[formId]?.name || formId;
		},
	});

	const paymentCols = getFormPaymentColDefs();
	cols = cols.concat(paymentCols);

	// get all forms in order, active
	await DataUtils.loadTenant(context);
	let forms: IForm[] = await DataUtils.getForms(true);
	forms = forms.filter((f: IForm) => formIds.includes(f.id));
	let formsGridContext: IFormsGridContext = {
		formConfigMap: {},
	};

	await asyncForEach(forms, async (form: IForm) => {
		if (!isSystemForm(form.id) && !!formIds.find((id: string) => id === form.id)) {
			const formConfig: IForm = await getForm(form.id, context.clientFirebase.getFirebase()).catch((e) => {
				console.error(e);
				return null;
			});
			formsGridContext.formConfigMap[form.id] = formConfig;
			const formColDefs: ColDef[] = getColDefsFromForm(formConfig);
			cols = cols.concat(formColDefs);
		}
	});

	return {
		colDefs: cols,
		formsGridContext,
	};
}

export function getFormPaymentColDefs(): ColDef[] {
	const cols: ColDef[] = [];
	cols.push(getColDef({ id: "paid", order: 1, fieldType: "boolean", label: "Paid" }));
	const stripeOrderCol: ColDef = getColDef({
		id: "stripeOrderNo",
		order: 2,
		fieldType: "link",
		label: "Stripe Order",
	});
	const ecwidOrderNoCol: ColDef = getColDef({
		id: "ecwidOrderNo",
		order: 3,
		fieldType: "link",
		label: "Ecwid Order",
	});
	cols.push({
		...getColDef({
			id: "stripeOrderNo",
			order: 2,
			fieldType: "collection",
			collectionName: "stripe_orders",
			label: "Stripe Order",
			findDrillItem: (item, props): boolean => {
				return props.value === item.id;
			},
		} as ISelectFormField),
		colId: "stripeOrderNoOrders",
		headerName: "Stripe Order",
	});
	cols.push({
		...stripeOrderCol,
		headerName: "Stripe Order Link",
		cellRendererFramework: (params: ICellRendererParams) => {
			if (!params.value || (params.data as OrderDetails)?.testOrder) return <span />;
			return (
				<a
					href={`https://dashboard.stripe.com/search?query=${params.value}`}
					className="text-xs"
					target="_blank"
				>
					See Order #{params.value} in Stripe
				</a>
			);
		},
	});
	cols.push({
		...getColDef({
			id: "ecwidOrderNo",
			order: 3,
			fieldType: "collection",
			collectionName: "orders",
			label: "Ecwid Order",
			findDrillItem: (item, props): boolean => {
				return props.value === item.orderNumber;
			},
		} as ISelectFormField),
		colId: "ecwidOrderNoOrders",
		headerName: "Ecwid Order",
	});
	cols.push({
		...ecwidOrderNoCol,
		headerName: "Ecwid Order Link",
		cellRendererFramework: (params: ICellRendererParams) => {
			if (!params.value) return <span />;
			return (
				<a
					href={`https://my.ecwid.com/store/${process.env.REACT_APP_ECWID_STORE_ID}#order:id=${params.value}&return=orders`}
					className="text-xs"
					target="_blank"
				>
					See Order #{params.value} in Ecwid
				</a>
			);
		},
	});
	return cols;
}

export const getColDefsFromObject = (data: any, collectionName?: string): ColDef[] => {
	const columnDefs: ColDef[] = [];
	if (!data) return [];

	const getColDef = (data: any, propPrefix?: string) => {
		for (let prop in data) {
			const field: string = propPrefix ? `${propPrefix}.${prop}` : prop;
			const title: string = propPrefix ? `${propPrefix} ${prop}` : prop;
			let colDef: ColDef = {
				colId: field,
				field: field,
				headerName: title,
				headerTooltip: title,
			};
			switch (typeof data[prop]) {
				case "boolean": {
					colDef = getBooleanColDef(colDef);
					columnDefs.push(colDef);
					break;
				}
				case "number": {
					if (prop === "created_at" || prop === "updated_at") {
						colDef = { ...getDateColDef(prop), ...colDef };
					} else if (prop === "amount" && collectionName === "stripe_orders") {
						colDef = {
							...NUMERIC_COL_DEF,
							...colDef,
							valueGetter: (params: ValueGetterParams) => {
								const val: number = params.getValue(colDef.field);
								if (!val) return val;
								return `$${(val / 100).toFixed(2)}`;
							},
						};
					} else {
						colDef = { ...NUMERIC_COL_DEF, ...colDef };
					}
					columnDefs.push(colDef);
					break;
				}
				case "object": {
					// TODO "object dana fix"
					// if (!Array.isArray(data[prop]) && data[prop] !== null) {
					// 	// is object
					// 	getColDef(data[prop], prop);
					// 	break;
					// } else {
					// 	columnDefs.push(colDef);
					// }
					break;
				}
				case "string":
				default: {
					if (colDef.field === "desc" || colDef.field === "description") {
						colDef = getDescriptionColConfig(colDef);
					}
					columnDefs.push(colDef);
				}
			}
		}
	};

	getColDef(data);
	return columnDefs;
};

export const getDescriptionColConfig = (col: ColDef): ColDef => {
	return {
		...col,
		fieldType: "textarea",
		suppressAutoSize: true,
		width: 300,
		tooltipComponent: "customTooltip",
		tooltipField: col.field,
		tooltipComponentParams: {
			field: col.field,
			tooltipDisplayMode: CustomTooltipDisplayMode.Content,
			renderContent: (params) => params.data[col.field],
		},
	} as ColDef;
};

export const getFieldConfigurationMap = async (
	clientFirebase: ClientFirebase,
	collectionName: string,
	cmsRoleContext?: ICMSRoleContext,
	fromFirewire?: boolean
): Promise<ColumnConfigurationMap> => {
	const colConfig: ColumnConfigurationMap =
		collectionName === "forms"
			? await getFormColDefinitions(fromFirewire, clientFirebase)
			: await getDocument(clientFirebase, "cms_editable", collectionName);
	console.log("Column configuration retrieved:", colConfig);
	if (cmsRoleContext) {
		for (let field in colConfig) {
			if (!cmsRoleContext.hasPermission(colConfig[field].permission)) {
				delete colConfig[field];
			}
		}
	}
	return colConfig;
};

export const getCMSColsConfig = async (
	clientFirebase: ClientFirebase,
	collectionName: string,
	cmsRolsContext: ICMSRoleContext,
	data?: any[],
	withIdCol?: boolean
): Promise<ColDef[]> => {
	const colConfig: ColumnConfigurationMap = await getFieldConfigurationMap(clientFirebase, collectionName);
	return getColDefsFromConfig(colConfig, collectionName, cmsRolsContext, data, !!colConfig, withIdCol);
};

export const getColDefsFromConfig = (
	colConfig: ColumnConfigurationMap,
	collectionName: string,
	cmsRoleContext?: ICMSRoleContext,
	data?: any[],
	getConfigFromObject?: boolean,
	withIdCol?: boolean
): ColDef[] => {
	let columnDefs: ColDef[] = [];
	if (colConfig) {
		let fields: IFormField[] = [];
		for (let key in colConfig) {
			fields.push({ ...colConfig[key], id: key } as IFormField);
		}
		if (cmsRoleContext) {
			fields = fields.filter((field: IFormField) => cmsRoleContext.hasPermission(field.permission));
		}
		fields = orderby(fields, (field: IFormField) => field.order);
		columnDefs = getColDefsFromForm({ fields, id: collectionName } as any);
	}
	if ((!colConfig || getConfigFromObject) && data?.length) {
		columnDefs = [...getColDefsFromObject(data[0], collectionName), ...columnDefs];
	}

	if (withIdCol) return [ID_COL_DEF, ...columnDefs];
	console.log("getColDefsFromConfig(): Returning columnDefs:", columnDefs);
	return columnDefs;
};
